import React, { useState, useEffect } from 'react';
import { useProjects } from '../contexts/ProjectContext';
import { useAuth } from '../contexts/AuthContext';
import Icon from './AppIcon';
import UploadedImageThumb from './UploadedImageThumb';
import { generationMetadataService, storageService, savedDesignsService, projectsService } from '../services/supabaseService';

/**
 * Reusable Asset Library panel for all tools.
 */
const LOAD_MORE_COUNT = 10;

const AssetLibraryPanel = ({
  libraryAssets,
  setLibraryAssets,
  generatedImages = [],
  setGeneratedImages,
  onImageClick,
  onImageDeleted,
  showDragHint = true,
  currentDesignId,
  currentProjectId,
}) => {
  const { uploadedMedia } = useProjects();
  const { user } = useAuth();
  const [showProjectBrowser, setShowProjectBrowser] = useState(false);
  const [confirmDeleteIdx, setConfirmDeleteIdx] = useState(null);
  const [visibleGenCount, setVisibleGenCount] = useState(LOAD_MORE_COUNT);
  const [dbProjects, setDbProjects] = useState([]);
  const [projectDesigns, setProjectDesigns] = useState({});
  const [pinnedFolders, setPinnedFolders] = useState([]); // [{ type: 'uploaded'|'project', id, name }]
  const [pinnedVisibleCounts, setPinnedVisibleCounts] = useState({});
  const [projectPage, setProjectPage] = useState(1);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const PROJECTS_PER_PAGE = 5;

  // Load projects from DB when modal opens
  useEffect(() => {
    if (!showProjectBrowser || !user?.id) return;
    setProjectPage(1);
    setLoadingProjects(true);
    const loadProjects = async () => {
      try {
        const { data } = await projectsService.getProjects(user.id, {}, 1, 100);
        setDbProjects(data || []);
      } catch (err) {
        console.error('Failed to load projects:', err);
      } finally {
        setLoadingProjects(false);
      }
    };
    loadProjects();
  }, [showProjectBrowser, user?.id]);

  // Load designs for pinned project folders that haven't been loaded yet
  useEffect(() => {
    pinnedFolders.forEach((folder) => {
      if (folder.type !== 'project' || projectDesigns[folder.id]) return;
      const loadDesigns = async () => {
        try {
          const designs = await savedDesignsService.getDesigns(folder.id);
          const urls = (designs || []).map((d) => d.imageUrl).filter(Boolean);
          setProjectDesigns((prev) => ({ ...prev, [folder.id]: urls }));
        } catch (err) {
          console.error('Failed to load designs for pinned folder:', err);
        }
      };
      loadDesigns();
    });
  }, [pinnedFolders]);

  const togglePinnedFolder = (folder) => {
    setPinnedFolders((prev) => {
      const exists = prev.find((f) => f.id === folder.id);
      if (exists) return prev.filter((f) => f.id !== folder.id);
      return [...prev, folder];
    });
  };

  const getPinnedFolderImages = (folder) => {
    if (folder.type === 'uploaded') return uploadedMedia || [];
    return projectDesigns[folder.id] || [];
  };

  const handleDragStart = (e, imageUrl, type) => {
    if (!imageUrl || !type) return;
    try {
      e.dataTransfer.effectAllowed = 'copy';
      e.dataTransfer.setData('text/plain', imageUrl);
      e.dataTransfer.setData('imageUrl', imageUrl);
      e.dataTransfer.setData('imageType', type);
    } catch (error) {
      console.error('AssetLibrary drag error:', error);
    }
  };

  const handleDeleteImage = async (idx) => {
    const imageUrl = generatedImages[idx];
    const remaining = generatedImages.filter((_, i) => i !== idx);
    // ปิด popup และลบจาก state ทันที
    setConfirmDeleteIdx(null);
    setGeneratedImages?.((prev) => prev.filter((_, i) => i !== idx));
    // แจ้ง tool ว่ารูปถูกลบ เพื่อให้ fallback result image
    onImageDeleted?.(remaining, imageUrl);
    // อัพเดท project thumbnail เป็นรูปล่าสุดที่เหลือ
    if (currentProjectId) {
      try { await projectsService.updateProject(currentProjectId, { thumbnail: remaining[0] || null }); } catch (err) { console.error('Failed to update project thumbnail:', err); }
    }
    // ลบจาก backend แบบ fire-and-forget
    if (imageUrl) {
      try { await storageService.deleteImage(imageUrl); } catch (err) { console.error('Failed to delete from storage:', err); }
      try { await savedDesignsService.deleteByImageUrl(imageUrl); } catch (err) { console.error('Failed to delete from saved_designs:', err); }
    }
    if (currentDesignId && imageUrl) {
      try { await generationMetadataService.removeFromUsedMedia(currentDesignId, imageUrl); } catch (err) { console.error('Failed to remove from used_media:', err); }
    }
  };

  return (
    <>
      <div className="glassmorphic-card rounded-3xl p-5">
        <div className="mb-3 px-2">
          <span className="text-white/70 text-sm">
            Asset Library
          </span>
        </div>

        <button
          onClick={() => setShowProjectBrowser(true)}
          className="w-full glassmorphic-button-dark rounded-2xl py-3 text-white font-medium flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform mb-3"
        >
          <Icon name="FolderOpen" size={18} />
          Choose from storage
        </button>

        {showDragHint && (
          <p className="text-white text-sm font-semibold italic mt-4 mb-3 flex items-center gap-2 px-2">
            <Icon name="GripVertical" size={16} className="text-orange-400 flex-shrink-0" />
            <span>Grab & drop images into upload areas above</span>
          </p>
        )}

        <div className="flex flex-wrap gap-2 max-h-[220px] overflow-y-auto overflow-x-hidden custom-scrollbar pr-1 pb-1">
          {generatedImages?.slice(0, visibleGenCount).map((img, idx) => (
            <div
              key={`gen-${idx}`}
              draggable
              data-drag-url={img}
              data-drag-type="generated"
              onDragStart={(e) => handleDragStart(e, img, 'generated')}
              onClick={() => onImageClick?.(img)}
              className="cursor-grab active:cursor-grabbing"
              style={{ touchAction: 'none' }}
            >
              <UploadedImageThumb
                src={img}
                alt={`Generated image ${idx + 1}`}
                onRemove={setGeneratedImages ? () => setConfirmDeleteIdx(idx) : undefined}
                imgClass="max-w-[80px] max-h-[60px] object-contain rounded-lg border border-white/10"
                height="h-auto"
                previewWidth="w-[200px]"
              />
            </div>
          ))}
          {generatedImages?.length > visibleGenCount && (
            <button
              onClick={() => setVisibleGenCount((prev) => prev + LOAD_MORE_COUNT)}
              className="w-full py-2 text-xs text-orange-400 hover:text-orange-300 transition-colors font-medium"
            >
              Load more ({generatedImages.length - visibleGenCount} remaining)
            </button>
          )}
        </div>

        {/* Pinned Folder Sections */}
        {pinnedFolders.map((folder) => {
          const images = getPinnedFolderImages(folder);
          const visCount = pinnedVisibleCounts[folder.id] || LOAD_MORE_COUNT;
          return (
            <div key={`pinned-${folder.id}`} className="mt-4">
              <div className="flex items-center justify-between mb-2 px-2">
                <div className="flex items-center gap-2">
                  <Icon name={folder.type === 'uploaded' ? 'Upload' : 'Folder'} size={14} className="text-orange-400" />
                  <span className="text-white/70 text-xs font-medium truncate max-w-[160px]">{folder.name}</span>
                  <span className="text-white/30 text-xs">{images.length}</span>
                </div>
                <button
                  onClick={() => setPinnedFolders((prev) => prev.filter((f) => f.id !== folder.id))}
                  className="text-white/30 hover:text-white/60 transition-colors"
                >
                  <Icon name="X" size={14} />
                </button>
              </div>
              <div className="flex flex-wrap gap-2 max-h-[220px] overflow-y-auto overflow-x-hidden custom-scrollbar pr-1 pb-1">
                {images.slice(0, visCount).map((img, idx) => (
                  <div
                    key={`pinned-${folder.id}-${idx}`}
                    draggable
                    data-drag-url={img}
                    data-drag-type={folder.type === 'uploaded' ? 'uploaded' : 'project'}
                    onDragStart={(e) => handleDragStart(e, img, folder.type === 'uploaded' ? 'uploaded' : 'project')}
                    onClick={() => onImageClick?.(img)}
                    className="cursor-grab active:cursor-grabbing"
                    style={{ touchAction: 'none' }}
                  >
                    <UploadedImageThumb
                      src={img}
                      alt={`${folder.name} image ${idx + 1}`}
                      imgClass="max-w-[80px] max-h-[60px] object-contain rounded-lg border border-white/10"
                      height="h-auto"
                      previewWidth="w-[200px]"
                    />
                  </div>
                ))}
                {images.length > visCount && (
                  <button
                    onClick={() => setPinnedVisibleCounts((prev) => ({
                      ...prev,
                      [folder.id]: (prev[folder.id] || LOAD_MORE_COUNT) + LOAD_MORE_COUNT,
                    }))}
                    className="w-full py-2 text-xs text-orange-400 hover:text-orange-300 transition-colors font-medium"
                  >
                    Load more ({images.length - visCount} remaining)
                  </button>
                )}
                {images.length === 0 && (
                  <p className="text-white/30 text-xs text-center py-2 w-full">No images</p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Confirm Delete Modal */}
      {confirmDeleteIdx !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setConfirmDeleteIdx(null)}
          />
          <div className="relative glassmorphic-card rounded-[24px] border border-[rgba(254,131,56,0.3)] p-6 max-w-sm w-[90%]">
            <div className="text-center mb-4">
              <Icon name="AlertTriangle" size={40} className="text-orange-500 mx-auto mb-3" />
              <h3 className="text-white text-lg font-semibold">Discard this image?</h3>
              <p className="text-white/50 text-sm mt-2">This will permanently delete the image from storage. This action cannot be undone.</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmDeleteIdx(null)}
                className="flex-1 px-4 py-2.5 rounded-xl border border-white/10 text-white/70 hover:bg-white/5 transition-all text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteImage(confirmDeleteIdx)}
                className="flex-1 px-4 py-2.5 rounded-xl bg-red-500/20 border border-red-500/30 text-red-400 hover:bg-red-500/30 transition-all text-sm font-medium"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Project Browser Modal */}
      {showProjectBrowser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setShowProjectBrowser(false)}
          />
          <div className="relative w-[90%] max-w-lg max-h-[80vh] glassmorphic-card rounded-[24px] border border-[rgba(254,131,56,0.3)] p-6 flex flex-col">
            <button
              onClick={() => setShowProjectBrowser(false)}
              className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors z-10"
            >
              <Icon name="X" size={24} />
            </button>

            <h2 className="text-white text-lg font-semibold mb-1">Choose from storage</h2>
            <p className="text-white/40 text-sm mb-4">Click a folder to pin it below your asset library</p>

            <div className="overflow-y-auto flex-1 pr-1 custom-scrollbar space-y-2">
              {/* Uploaded Media Folder */}
              <button
                onClick={() => togglePinnedFolder({ type: 'uploaded', id: '__uploaded__', name: 'Uploaded Media' })}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-2xl border transition-colors hover:bg-white/5 ${pinnedFolders.some((f) => f.id === '__uploaded__') ? 'border-orange-500/50 bg-orange-500/5' : 'border-orange-500/20'}`}
              >
                <div className="flex items-center gap-3">
                  <Icon name="Upload" size={18} className="text-orange-500" />
                  <span className="text-white text-sm font-medium">Uploaded Media</span>
                  <span className="text-white/30 text-xs">{uploadedMedia?.length || 0} images</span>
                </div>
                {pinnedFolders.some((f) => f.id === '__uploaded__') ? (
                  <span className="text-orange-400 text-xs font-medium">✓ pinned</span>
                ) : (
                  <Icon name="Plus" size={16} className="text-white/30" />
                )}
              </button>

              {/* Project Folders from DB (exclude current project) — paginated 5/page */}
              {loadingProjects ? (
                <div className="flex items-center justify-center gap-2 py-6 text-white/50 text-sm">
                  <Icon name="Loader2" size={16} className="animate-spin" />
                  <span>Loading projects...</span>
                </div>
              ) : (() => {
                const filtered = dbProjects.filter((proj) => proj.id !== currentProjectId);
                const totalPages = Math.max(1, Math.ceil(filtered.length / PROJECTS_PER_PAGE));
                const page = Math.min(projectPage, totalPages);
                const start = (page - 1) * PROJECTS_PER_PAGE;
                const pageItems = filtered.slice(start, start + PROJECTS_PER_PAGE);

                const getPageNumbers = () => {
                  if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);
                  const pages = [];
                  if (page <= 3) {
                    pages.push(1, 2, 3, 4, '...', totalPages);
                  } else if (page >= totalPages - 2) {
                    pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
                  } else {
                    pages.push(1, '...', page - 1, page, page + 1, '...', totalPages);
                  }
                  return pages;
                };

                return (
                  <>
                    {pageItems.map((proj) => {
                      const isPinned = pinnedFolders.some((f) => f.id === proj.id);
                      return (
                        <button
                          key={proj.id}
                          onClick={() => togglePinnedFolder({ type: 'project', id: proj.id, name: proj.name })}
                          className={`w-full flex items-center justify-between px-4 py-3 rounded-2xl border transition-colors hover:bg-white/5 ${isPinned ? 'border-orange-500/50 bg-orange-500/5' : 'border-white/10'}`}
                        >
                          <div className="flex items-center gap-3">
                            <Icon name="Folder" size={18} className="text-orange-500" />
                            <span className="text-white text-sm font-medium truncate max-w-[200px]">{proj.name}</span>
                          </div>
                          {isPinned ? (
                            <span className="text-orange-400 text-xs font-medium">✓ pinned</span>
                          ) : (
                            <Icon name="Plus" size={16} className="text-white/30" />
                          )}
                        </button>
                      );
                    })}

                    {filtered.length === 0 && (
                      <p className="text-white/30 text-xs text-center py-4">No other projects</p>
                    )}

                    {totalPages > 1 && (
                      <div className="flex items-center justify-center gap-1 pt-3 flex-wrap">
                        <button
                          onClick={() => setProjectPage((p) => Math.max(1, p - 1))}
                          disabled={page === 1}
                          className="px-2.5 py-1.5 rounded-lg text-xs text-white/70 border border-white/10 hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        >
                          <Icon name="ChevronLeft" size={14} />
                        </button>

                        {getPageNumbers().map((p, idx) =>
                          p === '...' ? (
                            <span key={`ellipsis-${idx}`} className="px-1.5 text-white/30 text-xs">…</span>
                          ) : (
                            <button
                              key={p}
                              onClick={() => setProjectPage(p)}
                              className={`min-w-[28px] px-2 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                p === page
                                  ? 'bg-orange-500/20 border border-orange-500/40 text-orange-300'
                                  : 'border border-white/10 text-white/70 hover:bg-white/5'
                              }`}
                            >
                              {p}
                            </button>
                          )
                        )}

                        <button
                          onClick={() => setProjectPage((p) => Math.min(totalPages, p + 1))}
                          disabled={page === totalPages}
                          className="px-2.5 py-1.5 rounded-lg text-xs text-white/70 border border-white/10 hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        >
                          <Icon name="ChevronRight" size={14} />
                        </button>
                      </div>
                    )}
                  </>
                );
              })()
              }
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AssetLibraryPanel;
